import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./AdminCustomRequests.css";
import getApiBase from "../utils/apiBase";
import { resolveImageUrl } from "../utils/resolveImageUrl";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";

const API_BASE = getApiBase();
const STATUSES = [
  "awaiting_upload",
  "submitted",
  "new",
  "reviewing",
  "confirmed",
  "quoted",
  "deposit_paid",
  "in_production",
  "completed",
  "cancelled",
];

const requestErrorKey = (error, fallback) => {
  if (error.status === 401 || error.status === 403) return "admin.loginRequired";
  if (error.status === 404) return "admin.requests.notFound";
  if (error.status === 429) return "admin.requests.rateLimited";
  if (error.status === 400) return "admin.requests.invalidRequest";
  return fallback;
};

const notificationStatusKey = (notifications, sentKey, errorKey) => {
  if (notifications?.[sentKey]) return "admin.requests.notification.sent";
  const error = notifications?.[errorKey] || "";
  if (/disabled|missing|configuration/i.test(error)) return "admin.requests.notification.disabled";
  if (error) return "admin.requests.notification.failed";
  return "admin.requests.notification.pending";
};

const notificationErrorKey = (error) => {
  if (!error) return "";
  if (/disabled/i.test(error)) return "admin.requests.notification.errorDisabled";
  if (/recipient.*missing/i.test(error)) return "admin.requests.notification.errorRecipient";
  if (/missing|configuration|not_configured/i.test(error)) return "admin.requests.notification.errorConfiguration";
  return "admin.requests.notification.errorFailed";
};

const NOTIFICATION_TYPE_KEYS = {
  wework: "admin.requests.wework",
  customer_email: "admin.requests.customerEmail",
  internal_email: "admin.requests.internalEmail",
};

const AdminCustomRequests = () => {
  const { t, formatDate, formatNumber } = useI18n();
  const [auth, setAuth] = useState(localStorage.getItem("adminAuth") || "");
  const [login, setLogin] = useState({ username: "", password: "" });
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState({ status: "new", adminNote: "" });
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState("");
  const displayDate = (value) => value ? formatDate(value, { dateStyle: "medium", timeStyle: "short" }) : "";
  const statusLabel = (value) => t(STATUSES.includes(value) ? `admin.requests.status.${value}` : "admin.requests.unknownStatus");
  const notificationLabel = (notifications, sentKey, errorKey) => t(notificationStatusKey(notifications, sentKey, errorKey));
  const notificationError = (error) => error ? t(notificationErrorKey(error)) : "";

  const authHeaders = useMemo(
    () => (auth ? { Authorization: `Basic ${auth}` } : {}),
    [auth],
  );

  const selectedRequest = useMemo(
    () => requests.find((item) => item.id === selectedId) || requests[0] || null,
    [requests, selectedId],
  );

  const fetchRequests = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/custom-requests`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        throw Object.assign(new Error(), { status: response.status });
      }
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      setRequests(items);
      setSelectedId((current) => {
        const queryId = new URLSearchParams(window.location.search).get("requestId");
        return current || queryId || items[0]?.id || "";
      });
    } catch (error) {
      setStatusMessage(requestErrorKey(error, "admin.requests.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [auth, authHeaders]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    if (selectedRequest) {
      setDraft({
        status: selectedRequest.status || "new",
        adminNote: selectedRequest.adminNote || "",
      });
    }
  }, [selectedRequest]);

  const handleLogin = (event) => {
    event.preventDefault();
    try {
      const token = btoa(`${login.username}:${login.password}`);
      localStorage.setItem("adminAuth", token);
      setAuth(token);
      setStatusMessage("admin.loggedIn");
    } catch {
      setStatusMessage("admin.requests.loginFailed");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    setAuth("");
    setLogin({ username: "", password: "" });
    setRequests([]);
    setSelectedId("");
    setStatusMessage("admin.loggedOut");
  };

  const saveRequest = async () => {
    if (!selectedRequest) return;
    setSaving(true);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/custom-requests/${selectedRequest.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(draft),
      });
      if (!response.ok) {
        throw Object.assign(new Error(), { status: response.status });
      }
      const data = await response.json();
      const updated = data.request;
      setRequests((current) => current.map((item) => (
        item.id === updated.id ? updated : item
      )));
      setStatusMessage("admin.requests.updated");
    } catch (error) {
      setStatusMessage(requestErrorKey(error, "admin.requests.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const resendNotification = async (type) => {
    if (!selectedRequest || notifying) return;
    setNotifying(type);
    setStatusMessage("");
    try {
      const response = await fetch(`${API_BASE}/api/admin/custom-requests/${selectedRequest.id}/notify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ type }),
      });
      if (!response.ok) {
        throw Object.assign(new Error(), { status: response.status });
      }
      const data = await response.json();
      const updated = data.request;
      setRequests((current) => current.map((item) => (
        item.id === updated.id ? updated : item
      )));
      setStatusMessage(data.result?.success ? "admin.requests.notificationSent" : notificationErrorKey(data.result?.code || data.result?.error) || "admin.requests.notificationFailed");
    } catch (error) {
      setStatusMessage(requestErrorKey(error, "admin.requests.resendFailed"));
    } finally {
      setNotifying("");
    }
  };

  return (
    <main className="admin-custom-requests">
      <Seo
        title={t("admin.requests.title")}
        description={t("admin.requests.description")}
        path="/admin/custom-requests"
        noindex
      />
      <header className="admin-custom-header">
        <div>
          <h1>{t("admin.requests.title")}</h1>
          <Link to="/admin">{t("admin.nav.products")}</Link>
          <Link to="/admin/users">{t("admin.nav.users")}</Link>
          <Link to="/admin/orders">{t("admin.nav.orders")}</Link>
          <Link to="/admin/custom-projects">{t("admin.nav.projects")}</Link>
        </div>
        {auth ? <button type="button" onClick={handleLogout}>{t("admin.logout")}</button> : null}
      </header>

      {!auth && (
        <form className="admin-custom-login" onSubmit={handleLogin}>
          <h2>{t("admin.loginTitle")}</h2>
          <label>
            {t("admin.username")}
            <input
              type="text"
              value={login.username}
              onChange={(event) => setLogin((current) => ({ ...current, username: event.target.value }))}
            />
          </label>
          <label>
            {t("admin.password")}
            <input
              type="password"
              value={login.password}
              onChange={(event) => setLogin((current) => ({ ...current, password: event.target.value }))}
            />
          </label>
          <button type="submit">{t("admin.login")}</button>
        </form>
      )}

      {auth && (
        <section className="admin-custom-layout">
          <aside className="admin-custom-list">
            <div className="admin-custom-list-header">
              <h2>{t("admin.requests.listTitle")}</h2>
              <button type="button" onClick={fetchRequests} disabled={loading}>
                {t(loading ? "admin.loading" : "admin.refresh")}
              </button>
            </div>
            {requests.length ? (
              requests.map((requestItem) => (
                <button
                  type="button"
                  className={`request-list-item${selectedRequest?.id === requestItem.id ? " selected" : ""}`}
                  key={requestItem.id}
                  onClick={() => setSelectedId(requestItem.id)}
                >
                  <strong>{requestItem.name || t("admin.requests.unnamedCustomer")}</strong>
                  <span>{requestItem.productName}</span>
                  <em>{statusLabel(requestItem.status || "new")}</em>
                  <small>{displayDate(requestItem.createdAt)}</small>
                  <span className="notification-summary">
                    {t("admin.requests.wework")}: {notificationLabel(requestItem.notifications, "weworkSent", "lastWeworkError")}
                  </span>
                  <span className="notification-summary">
                    {t("admin.requests.customerEmail")}: {notificationLabel(requestItem.notifications, "customerEmailSent", "lastCustomerEmailError")}
                  </span>
                  <span className="notification-summary">
                    {t("admin.requests.internalEmail")}: {notificationLabel(requestItem.notifications, "internalEmailSent", "lastInternalEmailError")}
                  </span>
                </button>
              ))
            ) : (
              <p className="admin-empty-state">{t("admin.requests.empty")}</p>
            )}
          </aside>

          <section className="admin-custom-detail">
            {selectedRequest ? (
              <>
                <div className="request-detail-heading">
                  <div>
                    <p>{t("admin.requests.requestId")}</p>
                    <h2>{selectedRequest.id}</h2>
                  </div>
                  <span className={`status-pill status-${selectedRequest.status || "new"}`}>
                    {statusLabel(selectedRequest.status || "new")}
                  </span>
                </div>

                <div className="request-fields">
                  <div>
                    <span>{t("admin.requests.customerName")}</span>
                    <strong>{selectedRequest.name}</strong>
                  </div>
                  <div>
                    <span>{t("admin.requests.email")}</span>
                    <a href={`mailto:${selectedRequest.email}`}>{selectedRequest.email}</a>
                  </div>
                  <div>
                    <span>{t("admin.requests.country")}</span>
                    <strong>{selectedRequest.country}</strong>
                  </div>
                  <div>
                    <span>{t("admin.requests.product")}</span>
                    <strong>{selectedRequest.productName}</strong>
                  </div>
                  <div>
                    <span>{t("admin.requests.color")}</span>
                    <strong>{selectedRequest.selectedColor || t("admin.notSet")}</strong>
                  </div>
                  <div>
                    <span>{t("admin.quantity")}</span>
                    <strong>{formatNumber(selectedRequest.quantity)}</strong>
                  </div>
                  <div>
                    <span>{t("admin.requests.createdAt")}</span>
                    <strong>{displayDate(selectedRequest.createdAt)}</strong>
                  </div>
                </div>

                <div className="request-media-grid">
                  <figure>
                    {selectedRequest.artworkUrl ? (
                      <img src={resolveImageUrl(selectedRequest.artworkUrl)} alt={t("admin.requests.uploadedArtwork")} />
                    ) : (
                      <div className="request-media-placeholder">{t("admin.requests.awaitingArtwork")}</div>
                    )}
                    <figcaption>
                      {t("admin.requests.uploadedArtwork")}
                      {selectedRequest.artworkUrl ? <a href={resolveImageUrl(selectedRequest.artworkUrl)} download>
                        {t("admin.requests.download")}
                      </a> : null}
                    </figcaption>
                  </figure>
                  <figure>
                    <img src={resolveImageUrl(selectedRequest.previewImageUrl)} alt={t("admin.requests.previewAlt")} />
                    <figcaption>
                      {t("admin.requests.generatedPreview")}
                      <a href={resolveImageUrl(selectedRequest.previewImageUrl)} download>
                        {t("admin.requests.download")}
                      </a>
                    </figcaption>
                  </figure>
                </div>

                <div className="request-notes">
                  <h3>{t("admin.requests.customerNotes")}</h3>
                  <p>{selectedRequest.notes || t("admin.requests.noNotes")}</p>
                </div>

                <div className="request-notifications">
                  <h3>{t("admin.requests.notifications")}</h3>
                  <div className="notification-grid">
                    <div>
                      <span>{t("admin.requests.wework")}</span>
                      <strong>{notificationLabel(selectedRequest.notifications, "weworkSent", "lastWeworkError")}</strong>
                      <small>{displayDate(selectedRequest.notifications?.weworkSentAt)}</small>
                      <p>{notificationError(selectedRequest.notifications?.lastWeworkError)}</p>
                    </div>
                    <div>
                      <span>{t("admin.requests.customerEmail")}</span>
                      <strong>{notificationLabel(selectedRequest.notifications, "customerEmailSent", "lastCustomerEmailError")}</strong>
                      <small>{displayDate(selectedRequest.notifications?.customerEmailSentAt)}</small>
                      <p>{notificationError(selectedRequest.notifications?.lastCustomerEmailError)}</p>
                    </div>
                    <div>
                      <span>{t("admin.requests.internalEmail")}</span>
                      <strong>{notificationLabel(selectedRequest.notifications, "internalEmailSent", "lastInternalEmailError")}</strong>
                      <small>{displayDate(selectedRequest.notifications?.internalEmailSentAt)}</small>
                      <p>{notificationError(selectedRequest.notifications?.lastInternalEmailError)}</p>
                    </div>
                  </div>
                  {selectedRequest.notifications?.errors?.length ? (
                    <ul className="notification-errors">
                      {selectedRequest.notifications.errors.map((errorItem, index) => (
                        <li key={`${errorItem.createdAt}-${index}`}>
                          <strong>{t(Object.prototype.hasOwnProperty.call(NOTIFICATION_TYPE_KEYS, errorItem.type) ? NOTIFICATION_TYPE_KEYS[errorItem.type] : "admin.requests.notificationOther")}</strong>
                          <span>{displayDate(errorItem.createdAt)}</span>
                          <p>{notificationError(errorItem.code || errorItem.message)}</p>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className="notification-actions">
                    <button type="button" onClick={() => resendNotification("wework")} disabled={Boolean(notifying)}>
                      {t(notifying === "wework" ? "admin.requests.sending" : "admin.requests.resendWework")}
                    </button>
                    <button type="button" onClick={() => resendNotification("internal_email")} disabled={Boolean(notifying)}>
                      {t(notifying === "internal_email" ? "admin.requests.sending" : "admin.requests.resendInternalEmail")}
                    </button>
                    <button type="button" onClick={() => resendNotification("customer_email")} disabled={Boolean(notifying)}>
                      {t(notifying === "customer_email" ? "admin.requests.sending" : "admin.requests.resendCustomerEmail")}
                    </button>
                  </div>
                </div>

                <div className="request-admin-editor">
                  <label>
                    {t("admin.status")}
                    <select
                      value={draft.status}
                      onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
                    >
                      {STATUSES.map((status) => (
                        <option value={status} key={status}>{statusLabel(status)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    {t("admin.requests.internalNote")}
                    <textarea
                      rows="6"
                      value={draft.adminNote}
                      onChange={(event) => setDraft((current) => ({ ...current, adminNote: event.target.value }))}
                    />
                  </label>
                  <button type="button" onClick={saveRequest} disabled={saving}>
                    {t(saving ? "admin.saving" : "admin.requests.saveChanges")}
                  </button>
                </div>
              </>
            ) : (
              <p className="admin-empty-state">{t("admin.requests.selectPrompt")}</p>
            )}
          </section>
        </section>
      )}

      {statusMessage && <p className="admin-custom-status">{t(statusMessage)}</p>}
    </main>
  );
};

export default AdminCustomRequests;
